import React, { useEffect, useState, useRef } from 'react';
import { feedback } from '@ecosystem/newron-design-system';
import { ContextSelectorMenu, ContextSettingSelectorProps, useEditContextSettings } from '@ecosystem/newron-core';
import { Market, PeriodAggregation, TimeFrame, TimeFrameOption } from '../types';
import { Entity, Periodicity } from '@ecosystem/newron-core/types';
import { PeriodSelector } from './periodSelector';
import { IncompletePeriod } from './incompletePeriod';
import { MissingSubscriptions } from './missingSubscriptions';
import { NavMenuButton } from '../common';
import { updateRecentMarkets } from '../market/recent';
import './focusPeriod.css';
import { getPeriodicity } from './weeksSelector.utils';
import { currentProposition } from '../../utils/currentProposition';
import { Proposition } from '../../utils/constants';
import { getSelectedMarkets, getTimeFrameOptions } from './util';
import { entityToTimeFrame, timeFrameToEntity, validateTimeFrame } from './timeFrame';
import { useMobile } from '../../providers/mobile/mobile.provider';
import { mobileViewportHeight } from '../constant';
import { hasBetaFeatureFlag } from '../../pages/utils';

const { DotsLoader } = feedback;

const HEIGHT = '450px';
const WIDTH = '400px';
const PENDING_AGGREGATION_KEY = 'market_focus_period_aggregation';

function FocusPeriodContextSelector({ isLoading, definition, onChange, onClose }: ContextSettingSelectorProps) {
  const [selectedMarkets, setSelectedMarkets] = useState<Market[] | undefined>();
  const [aggregation, setAggregation] = useState<PeriodAggregation>();
  const [timeFrame, setTimeFrame] = useState<TimeFrame>();
  const [timeFrameOptions, setTimeFrameOptions] = useState<TimeFrameOption[]>([]);
  const [notAllAvailableOption, setNotAllAvailableOption] = useState<TimeFrameOption | undefined>();
  const [ytdStartMonth, setYtdStartMonth] = useState<number>(1);
  const [isYtdLoading, setIsYtdLoading] = useState<boolean>(false);
  const { isMobile } = useMobile();

  const originalYtdStartMonthRef = useRef<number>(1);
  const ytdChangedRef = useRef<boolean>(false);

  const { getContextSettings, updateSettingsValues, clearSettingOptions } = useEditContextSettings();

  useEffect(() => {
    const fetch = async () => {
      const settings = await getContextSettings(['openMarkets', 'focusPeriod', 'timeFrame', 'cell', 'ytdStartMonth']);
      const selectedMarkets = getSelectedMarkets(settings);
      setSelectedMarkets(selectedMarkets);

      const timeFrame = entityToTimeFrame(settings['timeFrame'].values[0]);
      setTimeFrame(timeFrame);

      const contextYtdStartMonth = settings['ytdStartMonth']?.values?.[0]?.value
        ? parseInt(settings['ytdStartMonth'].values[0].value)
        : 1;
      setYtdStartMonth(contextYtdStartMonth);
      originalYtdStartMonthRef.current = contextYtdStartMonth;
      ytdChangedRef.current = false;

      const pendingAggregation = sessionStorage.getItem(PENDING_AGGREGATION_KEY);
      sessionStorage.removeItem(PENDING_AGGREGATION_KEY);

      const aggregation = (pendingAggregation as PeriodAggregation) || timeFrame.aggregation;
      setAggregation(aggregation);

      const timeFrameOptions = getTimeFrameOptions(settings, 'focusPeriod', selectedMarkets);
      setTimeFrameOptions(timeFrameOptions);
    };

    fetch();

    return () => {
      // Restore previous selected month if YTD start month was changed but no new YTD period was selected
      if (ytdChangedRef.current) {
        const restoreUpdates = {
          ytdStartMonth: [
            {
              value: originalYtdStartMonthRef.current.toString(),
              label: new Date(0, originalYtdStartMonthRef.current - 1).toLocaleString('default', { month: 'long' }),
            },
          ],
        };
        updateSettingsValues(restoreUpdates, false, true);
        sessionStorage.removeItem(PENDING_AGGREGATION_KEY);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition]);

  const handleAggregationChange = (value: PeriodAggregation) => {
    setNotAllAvailableOption(undefined);
    setAggregation(value);

    if (aggregation === PeriodAggregation.YTD && value !== PeriodAggregation.YTD) {
      ytdChangedRef.current = false; // Clear the change flag
    }
  };

  const handleTimeFrameSelect = (option: TimeFrameOption) => {
    if (!option.availableOnAll) {
      setNotAllAvailableOption(option);
      return;
    }

    const updates: Record<string, Entity[]> = {
      timeFrame: [timeFrameToEntity(option)],
    };

    if (aggregation === PeriodAggregation.YTD) {
      updates.ytdStartMonth = [
        {
          value: ytdStartMonth.toString(),
          label: new Date(0, ytdStartMonth - 1).toLocaleString('default', { month: 'long' }),
        },
      ];
      originalYtdStartMonthRef.current = ytdStartMonth;
      ytdChangedRef.current = false;
    }

    onChange(updates);
    onClose();
  };

  const handleNotAllAvailableTimeFrameSelect = (option: TimeFrameOption, removeAffectedMarkets: boolean) => {
    const updates: Record<string, Entity[]> = {
      timeFrame: [timeFrameToEntity(option)],
    };

    if (removeAffectedMarkets) {
      const affectedMarkets = selectedMarkets
        .filter(m =>
          m.periods.find(p => p.periodicity === option.periodicity && p.sequence < Number(option.endSequence))
        )
        .map(m => m.value);

      updates.cell = selectedMarkets
        .filter(m => !affectedMarkets.includes(m.value))
        .map(m => ({
          value: m.value,
          label: m.label,
        }));

      updateRecentMarkets(updates.cell);
    }

    onChange(updates);
    onClose();
  };

  const handleRemoveNonSubscribed = (marketsToRemove: Market[]) => {
    sessionStorage.setItem(PENDING_AGGREGATION_KEY, aggregation);
    const marketsToKeep = selectedMarkets.filter(m => !marketsToRemove.find(mtr => mtr.value === m.value));
    const validTimeFrame = validateTimeFrame(timeFrame, marketsToKeep, timeFrameOptions);
    const cellsToKeep = marketsToKeep.map(m => ({
      value: m.value,
      label: m.label,
    }));
    onChange({
      timeFrame: [timeFrameToEntity(validTimeFrame)],
      cell: cellsToKeep,
    });
    updateRecentMarkets(cellsToKeep);
  };

  const handleYTDStartMonthChange = async (startMonth: number) => {
    setIsYtdLoading(true);
    setYtdStartMonth(startMonth);
    ytdChangedRef.current = true;

    sessionStorage.setItem(PENDING_AGGREGATION_KEY, aggregation);

    const updates = {
      ytdStartMonth: [
        {
          value: startMonth.toString(),
          label: new Date(0, startMonth - 1).toLocaleString('default', { month: 'long' }),
        },
      ],
    };

    updateSettingsValues(updates, false, true);
    clearSettingOptions(['ytdStartMonth']);

    const ytdPeriods = await getContextSettings(['ytdStartMonth']);
    const ytdTimeFrameOptions = getTimeFrameOptions(ytdPeriods, 'ytdStartMonth', selectedMarkets);

    setTimeFrameOptions(prevOptions => {
      const nonYtdOptions = prevOptions.filter(opt => opt.aggregation !== PeriodAggregation.YTD);
      return [...nonYtdOptions, ...ytdTimeFrameOptions];
    });

    setIsYtdLoading(false);
  };

  let hasMissingSubscriptions = false;
  let hasCompleteData = false;

  if (selectedMarkets) {
    const periodicity = getPeriodicity(aggregation);
    const affectedMarkets = selectedMarkets.filter(m => !m.periods.some(p => p.periodicity === periodicity));
    hasCompleteData = selectedMarkets.length === affectedMarkets.length;
    hasMissingSubscriptions = affectedMarkets.length > 0;
  }

  const isDistributionPanel = currentProposition() === Proposition.DistributionPanel;
  const hideCustomWarning = isDistributionPanel && aggregation === PeriodAggregation.Custom;
  const isBeta = hasBetaFeatureFlag();
  const mobileStyle = { height: isBeta ? mobileViewportHeight : 'calc(100vh - 60px)', width: '100vw' };
  const webStyle = { height: HEIGHT, width: WIDTH };
  return (
    <ContextSelectorMenu>
      {!selectedMarkets && (
        <div style={isMobile ? mobileStyle : webStyle}>
          <DotsLoader stretch />
        </div>
      )}
      {selectedMarkets && (
        <div
          data-testid="focus-period-container"
          style={isMobile ? mobileStyle : webStyle}
          className="flex flex-row items-stretch"
        >
          <div className="relative w-32 flex-shrink-0 bg-light px-2 py-2 rounded-bl">
            <NavMenuButton
              label={PeriodAggregation.Week}
              isActive={aggregation === PeriodAggregation.Week}
              onClick={() => handleAggregationChange(PeriodAggregation.Week)}
            />
            <NavMenuButton
              label={PeriodAggregation.Month}
              isActive={aggregation === PeriodAggregation.Month}
              onClick={() => handleAggregationChange(PeriodAggregation.Month)}
            />
            <NavMenuButton
              label={PeriodAggregation.Quarter}
              isActive={aggregation === PeriodAggregation.Quarter || aggregation === PeriodAggregation.RollingQuarter}
              onClick={() => handleAggregationChange(PeriodAggregation.Quarter)}
            />
            <NavMenuButton
              label={PeriodAggregation.Year}
              isActive={[PeriodAggregation.Year, PeriodAggregation.MAT, PeriodAggregation.YTD].includes(aggregation)}
              onClick={() => handleAggregationChange(PeriodAggregation.Year)}
            />
            <hr />
            <NavMenuButton
              label={PeriodAggregation.Custom}
              isActive={[PeriodAggregation.Custom].includes(aggregation)}
              onClick={() => handleAggregationChange(PeriodAggregation.Custom)}
            />
          </div>
          {hasMissingSubscriptions && hasCompleteData && !hideCustomWarning && (
            <MissingSubscriptions
              isLoading={isLoading}
              onRemoveNonSubscribed={handleRemoveNonSubscribed}
              markets={selectedMarkets}
              periodicity={aggregation === PeriodAggregation.Week ? Periodicity.Weekly : Periodicity.Monthly}
            />
          )}
          {!hasMissingSubscriptions && notAllAvailableOption && (
            <IncompletePeriod
              onSelect={handleNotAllAvailableTimeFrameSelect}
              option={notAllAvailableOption}
              markets={selectedMarkets}
              onBack={() => setNotAllAvailableOption(undefined)}
            />
          )}
          {!notAllAvailableOption && (
            <PeriodSelector
              currentValue={timeFrame}
              markets={selectedMarkets}
              options={timeFrameOptions}
              aggregation={aggregation}
              ytdStartMonth={ytdStartMonth}
              isYtdLoading={isYtdLoading}
              onSelect={handleTimeFrameSelect}
              onAggregationChange={handleAggregationChange}
              handlePartialTimeFrameData={handleNotAllAvailableTimeFrameSelect}
              onYtdStartMonthChange={handleYTDStartMonthChange}
            />
          )}
        </div>
      )}
    </ContextSelectorMenu>
  );
}

export { FocusPeriodContextSelector };
